import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { RecordingProvider, useRecording } from '../context/RecordingContext';
import WaveSurfer from 'wavesurfer.js';
import { AudioWaveform } from '../components/AudioWaveform';
import { LyricsDisplay } from '../components/LyricsDisplay';
import { RecordingControls } from '../components/RecordingControls';
import { RecordingError } from '../components/RecordingError';
import { PlaybackModal } from '../components/RecordingsList';
import { Song } from '../services/SongService';
import { CircleAlert as AlertCircle, X, Settings, Save, Mic, Eye, FlipHorizontal, TestTube, Coins, RotateCcw, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DraggableVideoPreview } from '../components/DraggableVideoPreview';
import { TestRecordingModal } from '../components/TestRecordingModal';
import { WalkthroughOverlay } from '../components/WalkthroughOverlay';
import { JocelynScore } from './JocelynScore';
import { CountdownOverlay } from '../components/CountdownOverlay';
import { FFmpegService } from '../services/FFmpegService';
import { HeadsetCheckModal } from '../components/HeadsetCheckModal';
import { logDeviceCheck, linkDeviceCheckToRecording, linkDeviceOverrideToRecording, flagRecordingAudioQuality, didUserProceedWithoutHeadset, getCurrentUserId, logDeviceOverride, hashDeviceId, DetectedDevice } from '../services/DeviceService';
import { useUser } from '../context/UserContext';
import { VirtualCurrencyService } from '../services/VirtualCurrencyService';
import { getUserTournamentRecordingLock } from '../services/TournamentService';

interface LoadingState {
  audio: boolean;
  lyrics: boolean;
}

const PERFORMANCE_SCORING_COST = 10;
const coinService = new VirtualCurrencyService();

const RecordPageContent: React.FC = () => {
  const { songId } = useParams<{ songId: string }>();
  const [searchParams] = useSearchParams();
  const tournamentId = searchParams.get('tournament');
  const tournamentRound = searchParams.get('round') ? parseInt(searchParams.get('round')!) : null;
  const { songService } = useData();
  const navigate = useNavigate();
  const { user } = useUser();
  const { recording, startRecording, stopRecording, saveRecording, deleteRecording, onCountdownComplete } = useRecording();
  
  const [song, setSong] = useState<Song | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>(''); // Record track - vocals-only backing track
  const [practiceTrackUrl, setPracticeTrackUrl] = useState<string>(''); // Practice track - full mix with vocals
  const [lyrics, setLyrics] = useState<string>('');
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showExitModal, setShowExitModal] = useState(false);
  const [errors, setErrors] = useState<{ audio?: string; lyrics?: string; recording?: string }>({});
  const [loading, setLoading] = useState<LoadingState>({ audio: true, lyrics: true });
  const [showSettings, setShowSettings] = useState(false);
  const [pitch, setPitch] = useState(0);
  const [tempo, setTempo] = useState(1);
  const [volume, setVolume] = useState(1);
  const [showCamera, setShowCamera] = useState(false);
  const [showPlaybackModal, setShowPlaybackModal] = useState(false);
  const [useBackgroundBlur, setUseBackgroundBlur] = useState(false);
  const [videoMirrored, setVideoMirrored] = useState(true);
  const [showTestRecordingModal, setShowTestRecordingModal] = useState(false);
  const [showJocelynScore, setShowJocelynScore] = useState(false);
  const [savedRecordingId, setSavedRecordingId] = useState<string | null>(null);
  const [savedRecordingUrl, setSavedRecordingUrl] = useState<string | null>(null);
  const [savedVocalsOnlyUrl, setSavedVocalsOnlyUrl] = useState<string | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  const [showHeadsetCheck, setShowHeadsetCheck] = useState(false);
  const [pendingRecordingMode, setPendingRecordingMode] = useState<'audio' | 'video' | null>(null);
  const [showInsufficientCoinsModal, setShowInsufficientCoinsModal] = useState(false);
  const [coinBalance, setCoinBalance] = useState<number | null>(null);
  const [autoScoringTriggered, setAutoScoringTriggered] = useState(false);
  const [showReviewScreen, setShowReviewScreen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [scoringRequestFailed, setScoringRequestFailed] = useState(false);
  const reviewAudioRef = useRef<HTMLAudioElement | null>(null);
  const deviceCheckIdRef = useRef<string | null>(null);
  const deviceOverrideIdRef = useRef<string | null>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const currentTrackRef = useRef<'practice' | 'record'>('record');
  const [cameraFailed, setCameraFailed] = useState(false);
  const [tournamentLockReason, setTournamentLockReason] = useState<string | null>(null);
  const tournamentLockCheckedRef = useRef(false);

  useEffect(() => {
    if (!tournamentId || !user?.id || !songId || tournamentLockCheckedRef.current) return;
    tournamentLockCheckedRef.current = true;
    getUserTournamentRecordingLock(tournamentId, tournamentRound, songId)
      .then(({ recordedThisRound, songUsed }) => {
        if (recordedThisRound) {
          setTournamentLockReason(`You've already recorded your song for Round ${tournamentRound}. You can record again when the next round opens.`);
        } else if (songUsed) {
          setTournamentLockReason("You've already sung this song in this tournament. Please choose a different song.");
        }
      })
      .catch(() => {
        setTournamentLockReason("We couldn't confirm your tournament recording status. Please go back to the tournament and try again.");
      });
  }, [tournamentId, user?.id, songId, tournamentRound]);

  // Effect to check if audio is nearly finished and auto-stop recording
  useEffect(() => {
    if (wavesurferRef.current && duration > 0 && currentTime > 0 && recording.isRecording && songId && !recording.isProcessing) {
      const timeDifference = duration - currentTime;
      const threshold = 0.5;

      if (timeDifference < threshold) {
        setIsAudioPlaying(false);
        if (wavesurferRef.current) wavesurferRef.current.pause();
        stopRecording(songId, () => {
          setShowCamera(false);
        }, wavesurferRef.current, tournamentId, tournamentRound);
      }
    }
  }, [currentTime, duration, recording.isRecording, recording.isProcessing, songId]);

  // Handle camera preview
  const toggleCamera = () => {
    if (showCamera) {
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        videoStreamRef.current = null;
      }
      setShowCamera(false);
    } else {
      // Just show the camera - the preview component will handle getting the stream
      setShowCamera(true);
    }
  };

  // Wait for camera to be ready before starting recording
  const waitForCamera = (): Promise<MediaStream> => {
    return new Promise((resolve, reject) => {
      const checkInterval = 100; // Check every 100ms
      const maxWaitTime = 10000; // Maximum 10 seconds
      let elapsed = 0;

      const checkStream = setInterval(() => {
        if (videoStreamRef.current) {
          clearInterval(checkStream);
          resolve(videoStreamRef.current);
        } else {
          elapsed += checkInterval;
          if (elapsed >= maxWaitTime) {
            clearInterval(checkStream);
            reject(new Error('Camera initialization timed out'));
          }
        }
      }, checkInterval);
    });
  };

  // Toggle video mirroring
  const toggleMirroring = () => {
    setVideoMirrored(!videoMirrored);
  };

  // Cleanup camera on unmount
  useEffect(() => {
    // Preload FFmpeg on component mount
    FFmpegService.initFFmpeg().catch(error => {
      console.error('FFmpeg preload failed:', error);
    });

    return () => {
      // Stop camera preview
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach(track => track.stop());
        videoStreamRef.current = null;
        setShowCamera(false);
      }
    };
  }, []);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Function to load a specific track into the wavesurfer
  const loadTrack = async (trackType: 'practice' | 'record') => {
    if (!wavesurferRef.current) return;

    const targetUrl = trackType === 'practice' ? practiceTrackUrl : audioUrl;
    if (!targetUrl) {
      console.error(`${trackType} track URL not available`);
      return;
    }

    // Only reload if switching to a different track
    if (currentTrackRef.current !== trackType) {
      try {
        await wavesurferRef.current.load(targetUrl);
        currentTrackRef.current = trackType;
        console.log(`Loaded ${trackType} track`);
      } catch (error) {
        console.error(`Error loading ${trackType} track:`, error);
      }
    }
  };

  useEffect(() => {
    if (songId) {
      const loadSong = async () => {
        try {
          // Reset states on new load attempt
          setErrors({});
          setLoading({ audio: true, lyrics: true });

          const songData = await songService.getSong(songId);
          if (!songData) {
            throw new Error('Song not found');
          }
          setSong(songData);

          // Load audio tracks and lyrics separately to handle individual errors
          const loadAudio = async () => {
            try {
              // Load record track (vocals-only backing track for recording)
              const recordTrackData = await songService.getAudioUrl(songId);
              setAudioUrl(recordTrackData);

              // Load practice track (full mix with vocals for practice mode)
              try {
                const practiceTrackData = await songService.getFullMixUrl(songId);
                setPracticeTrackUrl(practiceTrackData);
              } catch (practiceError) {
                console.warn('Practice track not available, will use record track as fallback');
                setPracticeTrackUrl(recordTrackData);
              }
            } catch (error) {
              setErrors(prev => ({ ...prev, audio: 'Unable to load audio tracks. Please try again later.' }));
            } finally {
              setLoading(prev => ({ ...prev, audio: false }));
            }
          };

          const loadLyrics = async () => {
            try {
              const lyricsData = await songService.getLyrics(songId);
              setLyrics(lyricsData);
            } catch (error) {
              setErrors(prev => ({ ...prev, lyrics: 'Unable to load lyrics. Please try again later.' }));
            } finally {
              setLoading(prev => ({ ...prev, lyrics: false }));
            }
          };

          // Load both in parallel
          await Promise.all([loadAudio(), loadLyrics()]);

        } catch (error) {
          console.error('Error loading song:', error);
          setErrors({ audio: 'Unable to load the song. Please check if the song exists and try again.' });
          setLoading({ audio: false, lyrics: false });
        }
      };

      loadSong();
    }
  }, [songId]);

  const handleStartRecording = (mode: 'audio' | 'video') => {
    const videoStream = mode === 'video' ? videoStreamRef.current : undefined;
    if (!audioUrl) {
      setErrors(prev => ({ ...prev, recording: 'No backing track available. Please try again.' }));
      return;
    }

    if (mode === 'video' && !videoStream) {
      console.error('No video stream available for recording');
      setErrors(prev => ({ ...prev, recording: 'Camera not ready. Please try again.' }));
      return;
    }

    console.log('Starting recording with:', {
      mode,
      hasVideoStream: !!videoStream,
      videoTracks: videoStream?.getVideoTracks().length,
      audioUrl
    });
    startRecording(mode, videoStream, audioUrl, wavesurferRef.current || undefined);
  };

  // Handle stop recording button click - stop and proceed to review screen
  const handleStopRecordingClick = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.pause();
    }
    setIsAudioPlaying(false);
    stopRecording(songId || '', () => {
      setShowCamera(false);
    }, wavesurferRef.current, tournamentId, tournamentRound);
  };

  // Fetch coin balance when user is available
  useEffect(() => {
    if (user?.id) {
      coinService.getUserCoins(user.id).then(data => {
        setCoinBalance(data?.balance ?? 0);
      }).catch(() => setCoinBalance(0));
    }
  }, [user?.id]);

  // Show review screen when local blob is available (immediately after recording stops)
  useEffect(() => {
    if (recording.localBlobUrl && !recording.isRecording && !autoScoringTriggered) {
      setShowReviewScreen(true);
      setShowPlaybackModal(false);
    }
  }, [recording.localBlobUrl, recording.isRecording, autoScoringTriggered]);

  const handleSubmitRecording = async () => {
    console.log('[SCORING] handleSubmitRecording called');
    console.log('[SCORING] tournamentId:', tournamentId, '| user:', user?.id, '| songId:', songId);
    setShowReviewScreen(false);
    setAutoScoringTriggered(true);
    setUploadError(null);
    setScoringRequestFailed(false);
    setShowJocelynScore(true);

    let saved: Awaited<ReturnType<typeof saveRecording>>;
    try {
      console.log('[SCORING] Saving recording...');
      saved = await saveRecording(songId || '', tournamentId);
    } catch (err) {
      console.error('[SCORING] Upload failed:', err);
      setUploadError("We couldn't upload your recording. Please check your connection, close this window and try recording again.");
      return;
    }

    try {
      console.log('[SCORING] Recording saved:', { recordingId: saved.recordingId, recordingUrl: saved.recordingUrl?.substring(0, 80), vocalsOnlyUrl: saved.vocalsOnlyUrl?.substring(0, 80) || 'null' });
      setSavedRecordingId(saved.recordingId);
      setSavedRecordingUrl(saved.recordingUrl);
      setSavedVocalsOnlyUrl(saved.vocalsOnlyUrl);

      if (user?.id && songId) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        const payload = {
          user_id: user.id,
          song_id: songId,
          VoiceReference: song?.lead_vocal_url,
          VoiceRecording: saved.vocalsOnlyUrl || saved.recordingUrl,
          recording_id: saved.recordingId
        };
        console.log('[SCORING] Calling submit-score with payload:', { ...payload, VoiceReference: payload.VoiceReference?.substring(0, 60), VoiceRecording: payload.VoiceRecording?.substring(0, 60) });
        
        const response = await fetch(`${supabaseUrl}/functions/v1/submit-score`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseKey}`,
            'apikey': supabaseKey
          },
          body: JSON.stringify(payload)
        });

        console.log('[SCORING] submit-score response status:', response.status);
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          console.error('[SCORING] submit-score FAILED:', response.status, errorData);
          if (response.status === 402) {
            console.warn('[SCORING] Insufficient coins for auto-scoring');
          }
          if (tournamentId) setScoringRequestFailed(true);
        } else {
          const successData = await response.json().catch(() => null);
          console.log('[SCORING] submit-score SUCCESS:', successData);
          if (!tournamentId) {
            setCoinBalance(prev => prev !== null ? Math.max(0, prev - PERFORMANCE_SCORING_COST) : null);
          }
        }
      } else {
        console.log('[SCORING] Skipping submit-score. Conditions not met:', { tournamentId, userId: user?.id, songId });
      }
    } catch (err) {
      console.error('[SCORING] Error in handleSubmitRecording:', err);
      if (tournamentId) setScoringRequestFailed(true);
    }
  };

  const handleReRecord = async () => {
    setShowReviewScreen(false);
    try {
      await deleteRecording();
    } catch (err) {
      console.error('Error resetting for re-record:', err);
    }
  };

  useEffect(() => {
    if (!recording.recordingId || !deviceCheckIdRef.current) return;
    const checkId = deviceCheckIdRef.current;
    const recId = recording.recordingId;
    const overrideId = deviceOverrideIdRef.current;
    deviceCheckIdRef.current = null;
    deviceOverrideIdRef.current = null;

    linkDeviceCheckToRecording(checkId, recId).catch(() => {});

    if (overrideId) {
      linkDeviceOverrideToRecording(overrideId, recId).catch(() => {});
    }

    didUserProceedWithoutHeadset(checkId).then(proceeded => {
      if (proceeded) {
        flagRecordingAudioQuality(recId).catch(() => {});
      }
    });
  }, [recording.recordingId]);

  const handleOpenTestRecordingModal = () => {
    setShowTestRecordingModal(true);
  };

  const handleCameraError = () => {
    setCameraFailed(true);
    setShowCamera(false);
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach(track => track.stop());
      videoStreamRef.current = null;
    }
    if (pendingRecordingMode === 'video') {
      setPendingRecordingMode('audio');
    }
  };

  const handleHeadsetCheckProceed = async (headsetDetected: boolean, devices: DetectedDevice[], overrideDevice?: DetectedDevice) => {
    setShowHeadsetCheck(false);

    const userId = await getCurrentUserId();
    if (userId && songId && pendingRecordingMode) {
      const checkId = await logDeviceCheck({
        userId,
        songId,
        recordingType: pendingRecordingMode,
        headsetDetected,
        detectedDevices: devices,
        userProceededWithoutHeadset: !headsetDetected,
        overrideDevice,
      });
      deviceCheckIdRef.current = checkId;

      if (overrideDevice && checkId) {
        const idHash = await hashDeviceId(overrideDevice.deviceId);
        const overrideId = await logDeviceOverride({
          userId,
          deviceLabel: overrideDevice.label,
          deviceIdHash: idHash,
          recordingDeviceCheckId: checkId,
        });
        deviceOverrideIdRef.current = overrideId;
      }
    }

    if (pendingRecordingMode) {
      const mode = pendingRecordingMode;
      setPendingRecordingMode(null);

      if (mode === 'video' && !cameraFailed) {
        try {
          await waitForCamera();
        } catch {
          setCameraFailed(true);
          setShowCamera(false);
          if (videoStreamRef.current) {
            videoStreamRef.current.getTracks().forEach(track => track.stop());
            videoStreamRef.current = null;
          }
          setTimeout(() => {
            handleStartRecording('audio');
          }, 100);
          return;
        }
      }

      const finalMode = cameraFailed ? 'audio' : mode;
      setTimeout(() => {
        handleStartRecording(finalMode);
      }, 100);
    }
  };

  if (tournamentLockReason) {
    return (
      <div className="flex items-center justify-center h-screen p-4">
        <div className="card max-w-md w-full text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-amber-500/15 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-amber-400" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Recording locked</h2>
          <p className="text-white/70 mb-6">{tournamentLockReason}</p>
          <button
            onClick={() => navigate(`/tournaments/${tournamentId}`)}
            className="bg-kod-accent hover:bg-kod-accent-dark text-white py-3 px-6 rounded-lg transition-colors"
          >
            Back to Tournament
          </button>
        </div>
      </div>
    );
  }

  if (Object.keys(errors).length > 0 && !loading.audio && !loading.lyrics) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-4">
        <div className="flex items-center gap-2 text-red-500 mb-4">
          <AlertCircle className="w-6 h-6" />
          <h2 className="text-xl font-semibold">Error</h2>
        </div>
        <div className="text-center text-white/70">
          {errors.audio && <p className="mb-2">{errors.audio}</p>}
          {errors.lyrics && <p className="mb-2">{errors.lyrics}</p>}
          {errors.recording && <p>{errors.recording}</p>}
        </div>
      </div>
    );
  }

  if (!song || !songId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-tsl-accent"></div>
      </div>
    );
  }

  return (
    <div className="h-screen relative">
      {/* Video Preview - Full Screen */}
      {showCamera && (
        <DraggableVideoPreview
          onClose={toggleCamera}
          isRecording={recording.isRecording}
          useBackgroundBlur={useBackgroundBlur}
          mirrored={videoMirrored}
          fullScreen={true}
          onStreamReady={(stream) => {
            console.log('Camera stream ready:', stream);
            videoStreamRef.current = stream;
          }}
          onCameraError={handleCameraError}
        />
      )}

      {/* Error Display */}
      <RecordingError />
      
      {/* Preparation Overlay */}
      {recording.isPreparing && !recording.showCountdown && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-kod-accent rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div className="w-3 h-3 bg-kod-accent rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div className="w-3 h-3 bg-kod-accent rounded-full animate-bounce"></div>
              </div>
            </div>
            <p className="text-white text-xl font-medium">Starting Recording...</p>
          </div>
        </div>
      )}

      {/* Headset Check Modal */}
      {showHeadsetCheck && pendingRecordingMode && (
        <HeadsetCheckModal
          recordingType={pendingRecordingMode}
          onProceed={handleHeadsetCheckProceed}
        />
      )}

      {/* Countdown Overlay */}
      <CountdownOverlay
        isVisible={recording.showCountdown}
        onComplete={onCountdownComplete}
        countdownSeconds={3}
      />

      {/* Song Title and Artist - Only shown when not recording or preparing */}
      {!recording.isRecording && !isAudioPlaying && !recording.isPreparing && !recording.showCountdown && (
        <div className="absolute top-20 left-0 right-0 text-center z-30">
          <div className="bg-black/50 inline-block px-8 py-4 rounded-lg">
            <h1 className="text-3xl font-bold">{song.title}</h1>
            <p className="text-white/70 text-xl">{song.artist}</p>
          </div>
        </div>
      )}

      {/* Control Buttons - Top Right */}
      <div className="absolute top-6 right-6 z-40 flex flex-col gap-2">
        {/* Close button - only show if not recording */}
        {!recording.isRecording && (
          <button
            onClick={() => {
              console.log('Exit button clicked from header');
              setShowExitModal(true);
            }}
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors exit-button"
          >
            <X size={24} />
          </button>
        )}
        
        {/* Recording Controls - hide if recording is completed */}
        {!recording.recordingUrl && (
          <div className="mt-5">
          {/* Recording Controls */}
          <RecordingControls
            showCamera={showCamera}
            onToggleCamera={toggleCamera}
            videoStream={videoStreamRef.current}
            setShowCamera={setShowCamera}
            wavesurfer={wavesurferRef.current}
            songId={songId}
            tournamentId={tournamentId}
            tournamentRound={tournamentRound}
            onStartRecording={handleStartRecording}
            onStopRecordingClick={handleStopRecordingClick}
          />
            {!recording.isRecording && (
            <button
              onClick={() => setShowSettings(true)}
              className={`p-2 rounded-full transition-colors mt-4 ${
                recording.recordingUrl
                  ? 'bg-black/30 text-white/50 cursor-not-allowed'
                  : 'bg-black/50 hover:bg-black/70 text-white'
              }`}
              title="Settings"
              disabled={!!recording.recordingUrl}
            >
              <Settings size={24} />
            </button>
          )}
          </div>
        )}

        {/* Save button - only show if recording is completed */}
        {recording.recordingUrl && (
          <div className="mt-2">
            <button
              onClick={() => navigate('/recordings')}
              className="p-2 rounded-full transition-colors save-button bg-kod-accent hover:bg-kod-accent-dark text-white"
              title="Save and go to recordings"
            >
              <Save size={24} />
            </button>
          </div>
        )}
        
        {/* Camera and other settings - hide if recording is completed */}
        {!recording.isRecording && !recording.recordingUrl && (
          <div className="mt-2 flex flex-col gap-2">
            {showCamera && (
              <div className="camera-settings">
                <button
                  onClick={toggleMirroring}
                  className="p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                  title="Toggle mirroring"
                >
                  <FlipHorizontal size={24} />
                </button>
              </div>
            )}
            
            <button
              onClick={() => setUseBackgroundBlur(!useBackgroundBlur)}
              disabled={!showCamera}
              className={`p-2 rounded-full transition-colors ${
                !showCamera
                  ? 'bg-black/30 text-white/50 cursor-not-allowed'
                  : useBackgroundBlur
                    ? 'bg-kod-accent hover:bg-kod-accent-dark text-white'
                    : 'bg-black/50 hover:bg-black/70 text-white'
              }`}
              title={useBackgroundBlur ? "Disable background blur" : "Enable background blur"}
            >
              <Eye size={24} />
            </button>
          </div>
        )}
      </div>

      {/* Lyrics Overlay - Only shown when audio is playing or recording */}
      {(isAudioPlaying || recording.isRecording) && (
        <div className="absolute top-[20%] left-0 right-0 bottom-[20%] z-30 overflow-hidden">
          <LyricsDisplay
            lrcContent={lyrics}
            currentTime={currentTime}
          />
        </div>
      )}

      {/* Practice and Record buttons - Fixed at bottom */}
      {!recording.isRecording && !recording.isPreparing && !recording.showCountdown && (
        <div className="absolute bottom-32 left-0 right-0 flex justify-center z-40">
          <div className="bg-black/70 rounded-xl p-4 flex gap-4 practice-record-buttons">
            <button
              onClick={async () => {
                if (wavesurferRef.current) {
                  // Load the practice track (full mix with vocals)
                  await loadTrack('practice');
                  wavesurferRef.current.seekTo(0);
                  wavesurferRef.current.play();
                  setIsAudioPlaying(true);
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-10 rounded-full text-lg flex items-center practice-button"
            >
              Practice
            </button>
            <button
              onClick={async () => {
                // Check coin balance before allowing recording (tournaments are free)
                if (!tournamentId && coinBalance !== null && coinBalance < PERFORMANCE_SCORING_COST) {
                  setShowInsufficientCoinsModal(true);
                  return;
                }

                try {
                  await loadTrack('record');

                  if (cameraFailed) {
                    setIsAudioPlaying(true);
                    setPendingRecordingMode('audio');
                    setShowHeadsetCheck(true);
                  } else {
                    if (!showCamera) {
                      toggleCamera();
                    }
                    setIsAudioPlaying(true);
                    setPendingRecordingMode('video');
                    setShowHeadsetCheck(true);
                  }
                } catch (error) {
                  console.error('Failed to start recording:', error);
                  setErrors(prev => ({
                    ...prev,
                    recording: error instanceof Error ? error.message : 'Failed to initialize recording. Please check permissions.'
                  }));
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-10 rounded-full text-lg flex items-center record-button"
            >
              Record
            </button>
          </div>
        </div>
      )}
      

      
      {/* Song info and waveform - Fixed at bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-30">
        <div className="bg-black/70 p-4">
          {!audioUrl || loading.audio ? (
            <div className="flex justify-center items-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-kod-accent"></div>
            </div>
          ) : !errors.audio ? (
            <AudioWaveform 
              url={audioUrl} 
              onTimeUpdate={setCurrentTime}
              onDurationChange={setDuration}
              pitch={recording.isRecording ? 0 : pitch}
              tempo={recording.isRecording ? 1.0 : tempo}
              volume={volume}
              disableControls={recording.isRecording}
              onWaveformInit={(ws) => {
                console.log('RecordPage: Received wavesurfer instance');
                wavesurferRef.current = ws;
                
                // Listen for play/pause events
                ws.on('play', () => setIsAudioPlaying(true));
                ws.on('pause', () => setIsAudioPlaying(false));
                ws.on('finish', () => setIsAudioPlaying(false));
              }}
            />
          ) : (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
              {errors.audio}
            </div>
          )}
          <div className="flex justify-end mt-2 text-white/70 font-medium">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>
      </div>

      {/* Review Screen */}
      {showReviewScreen && recording.localBlobUrl && !recording.isRecording && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-gray-900 border border-white/10 rounded-2xl p-6 text-center">
            <h2 className="text-xl font-bold text-white mb-2">Review Your Performance</h2>
            <p className="text-white/60 text-sm mb-6">Listen back before submitting, or re-record if you want another take.</p>

            <div className="bg-gray-800/60 rounded-xl p-6 mb-6">
              <audio
                ref={reviewAudioRef}
                src={recording.localBlobUrl}
                controls
                className="w-full"
              />
            </div>

            <div className="flex gap-3 justify-center">
              <button
                onClick={handleReRecord}
                className="flex items-center gap-2 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
              >
                <RotateCcw size={18} />
                Re-record
              </button>
              <button
                onClick={handleSubmitRecording}
                className="flex items-center gap-2 px-5 py-3 rounded-xl bg-green-600 hover:bg-green-500 text-white font-medium transition-colors"
              >
                <Send size={18} />
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recording Playback */}
      {showPlaybackModal && recording.recordingUrl && !recording.isRecording && (
        <div className="fixed bottom-4 right-4 w-96 bg-kod-purple-800 rounded-lg overflow-hidden shadow-lg z-50">
          <PlaybackModal
            recording={{
              id: recording.recordingId || 'preview',
              title: 'Preview Recording',
              recording_url: recording.recordingUrl,
              recording_type: recording.mode || 'audio',
              song: {
                title: song.title,
                artist: song.artist,
                audio_url: audioUrl
              }
            }}
            onClose={() => {
              setShowPlaybackModal(false);
              const video = document.querySelector('video');
              const audio = document.getElementById('backing-track') as HTMLAudioElement;
              if (video) video.pause();
              if (audio) audio.pause();
            }}
          />
        </div>
      )}

      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-kod-purple-800 p-6 rounded-lg max-w-md w-full mx-4 border border-kod-purple-600">
            <h3 className="text-xl font-bold mb-4">Exit Recording Session?</h3>
            <p className="text-white/70 mb-6">
              Are you sure you want to exit? Any unsaved progress will be lost.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  console.log('Stay button clicked');
                  setShowExitModal(false);
                }}
                className="px-4 py-2 rounded-md bg-kod-purple-700 hover:bg-kod-purple-600 transition-colors"
              >
                Stay
              </button>
              {recording.recordingUrl ? (
                <button
                  onClick={async () => {
                    try {
                      console.log('Delete & Exit button clicked');
                      await deleteRecording();
                      console.log('Recording deleted successfully');
                      console.log('Navigating to dashboard...');
                      navigate('/dashboard');
                    } catch (error) {
                      console.error('Error deleting recording:', error);
                      console.log('Error occurred during deletion, still navigating to dashboard');
                      // Navigate anyway since user wants to leave
                      navigate('/dashboard');
                    }
                  }}
                  className="px-4 py-2 rounded-md bg-red-500 hover:bg-red-600 transition-colors"
                >
                  Delete & Exit
                </button>
              ) : (
              <button
                onClick={() => {
                  console.log('Exit button clicked (no recording)');
                  console.log('Navigating to dashboard...');
                  navigate('/dashboard');
                }}
                className="px-4 py-2 rounded-md bg-red-500 hover:bg-red-600 transition-colors"
              >
                Exit
              </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-kod-purple-800 p-6 rounded-lg max-w-md w-full mx-4 border border-kod-purple-600">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Playback Settings</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-tsl-purple-700 rounded-full"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Background Blur Toggle */}
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium">Background Blur</label>
                  <div className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={useBackgroundBlur}
                      onChange={() => setUseBackgroundBlur(!useBackgroundBlur)}
                      className="sr-only peer"
                      disabled={!showCamera}
                    />
                    <div className={`w-11 h-6 ${!showCamera ? 'bg-tsl-purple-700/50' : 'bg-tsl-purple-700 peer-checked:bg-tsl-accent'} rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}></div>
                  </div>
                </div>
                {!showCamera && (
                  <p className="text-white/50 text-xs mt-1">Enable camera first to use background blur</p>
                )}
              </div>
              
              {/* Video Mirroring Toggle */}
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium">Mirror Video</label>
                  <div className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={videoMirrored}
                      onChange={() => setVideoMirrored(!videoMirrored)}
                      className="sr-only peer"
                      disabled={!showCamera}
                    />
                    <div className={`w-11 h-6 ${!showCamera ? 'bg-tsl-purple-700/50' : 'bg-tsl-purple-700 peer-checked:bg-tsl-accent'} rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}></div>
                  </div>
                </div>
                {!showCamera && (
                  <p className="text-white/50 text-xs mt-1">Enable camera first to control mirroring</p>
                )}
                {showCamera && (
                  <p className="text-white/60 text-xs mt-1">
                    {videoMirrored 
                      ? "Mirrored view (like looking in a mirror)" 
                      : "Non-mirrored view (how others see you)"}
                  </p>
                )}
              </div>
              
              {/* Pitch Control */}
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium">Pitch</label>
                  <span className="text-sm text-white/70">{pitch > 0 ? `+${pitch}` : pitch} semitones</span>
                </div>
                <input
                  type="range"
                  min="-12"
                  max="12"
                  step="1"
                  value={pitch}
                  onChange={(e) => setPitch(Number(e.target.value))}
                  className="w-full h-2 bg-tsl-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-tsl-accent"
                />
              </div>
              
              {/* Tempo Control */}
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium">Tempo</label>
                  <span className="text-sm text-white/70">{Math.round(tempo * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.05"
                  value={tempo}
                  onChange={(e) => setTempo(Number(e.target.value))}
                  className="w-full h-2 bg-tsl-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-tsl-accent"
                />
              </div>
              
              {/* Volume Control */}
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium">Volume</label>
                  <span className="text-sm text-white/70">{Math.round(volume * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                  className="w-full h-2 bg-tsl-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-tsl-accent"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Recording Modal */}
      <TestRecordingModal 
        isOpen={showTestRecordingModal}
        onClose={() => setShowTestRecordingModal(false)}
        backingTrackUrl={audioUrl}
      />
      
      {/* Jocelyn Score Modal */}
      {showJocelynScore && (
        <JocelynScore
          overallScore={0}
          pitchAccuracy={0}
          rhythmAccuracy={0}
          styleScore={0}
          songId={songId}
          songTitle={song?.title}
          songArtist={song?.artist}
          userRecordingUrl={savedRecordingUrl || recording.recordingUrl}
          vocalsOnlyUrl={savedVocalsOnlyUrl || recording.vocalsOnlyUrl}
          originalVocalUrl={song?.lead_vocal_url}
          recordingId={savedRecordingId || recording.recordingId}
          autoScoring
          uploadError={uploadError}
          scoringRequestFailed={scoringRequestFailed}
          isTournament={Boolean(tournamentId)}
          onClose={() => {
            setShowJocelynScore(false);
            navigate(tournamentId ? `/tournaments/${tournamentId}` : '/recordings');
          }}
        />
      )}

      {/* Insufficient Coins Modal */}
      {showInsufficientCoinsModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-kod-purple-800 rounded-xl overflow-hidden max-w-sm w-full shadow-xl p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Coins size={32} className="text-amber-400" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-white text-center mb-2">Not Enough Coins</h2>
            <p className="text-white/70 text-center mb-2">
              You need at least <span className="font-bold text-amber-400">{PERFORMANCE_SCORING_COST} coins</span> to record and get scored.
            </p>
            <p className="text-white/50 text-sm text-center mb-6">
              Your current balance: <span className="font-semibold text-white">{coinBalance ?? 0} coins</span>
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowInsufficientCoinsModal(false);
                  navigate('/purchase-coins');
                }}
                className="w-full bg-kod-accent hover:bg-kod-accent-dark text-white font-bold py-3 px-4 rounded-lg transition-colors"
              >
                Get More Coins
              </button>
              <button
                onClick={() => setShowInsufficientCoinsModal(false)}
                className="w-full bg-kod-purple-700 hover:bg-kod-purple-600 text-white py-3 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const RecordPage: React.FC = () => {
  return (
    <RecordingProvider>
      <RecordPageContent />
    </RecordingProvider>
  );
};